import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, MapPin, Search, Loader } from 'lucide-react';
import { StcBinDto } from '../../types/stcBin';
import { stcBinService } from '../../services/stcBinService';
import { useAuth } from '../../contexts/AuthContext';

export interface LocationSearchResult {
  binCode: string;
  locationName: string;
  warehouseID?: string;
  description?: string;
  zoneID?: string;
  binSizeNo?: number;
  binTypeNo?: number;
  binGrpNo?: number;
}

interface LocationSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (location: LocationSearchResult) => void;
  title?: string;
  excludeLocation?: string; // For å ekskludere fra-lokasjon når vi søker til-lokasjon
  minSearchLength?: number; // Minimum antall tegn før søk utløses (default: 1)
  /** Begrens søket til ett lager (f.eks. valgt til-lager i lageroverføring). Uten verdi søkes alle lager. */
  warehouseID?: string;
  /** Visningsnavn for lageret i info-/feilmeldinger. Faller tilbake til warehouseID. */
  warehouseLabel?: string;
}

export const LocationSearchModal: React.FC<LocationSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  title = 'Søk lokasjon',
  excludeLocation,
  minSearchLength = 1,
  warehouseID,
  warehouseLabel
}) => {
  const { user } = useAuth();

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState<StcBinDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setLocations([]);
      setError(null);
      setHasSearched(false);
      setLoading(false);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    }
  }, [isOpen]);

  // Perform search - tries exact match first (FAST), then falls back to general search
  const performSearch = useCallback(async (term: string, companyNo: string) => {
    const trimmed = term.trim();
    if (!trimmed) {
      setLocations([]);
      setHasSearched(false);
      return;
    }

    // Respekter minSearchLength — hindre tunge søk på enkelttegn når kalleren krever 2+
    if (trimmed.length < minSearchLength) {
      setLocations([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }

    console.log('🔍 [LocationSearchModal] Starting search for:', term, 'companyNo:', companyNo);

    const searchStartTime = performance.now();

    setLoading(true);
    setError(null);

    try {
      // OPTIMIZATION: Try exact match first (MUCH faster - single row lookup)
      console.log('🔍 [LocationSearchModal] Calling getByExactBinCode...');
      const exactMatch = await stcBinService.getByExactBinCode(companyNo, term.trim());
      const searchEndTime = performance.now();
      console.log(`⏱️ [LocationSearchModal] Search completed in ${Math.round(searchEndTime - searchStartTime)}ms`);

      // Eksakt treff gjelder bare når det ligger i valgt lager (hvis lager er satt). Ellers faller vi
      // til det generelle søket, som er lagerfiltrert på server — samme kode kan finnes i flere lager.
      const exactMatchInScope =
        !!exactMatch && (!warehouseID || String(exactMatch.warehouseID) === String(warehouseID));

      if (exactMatch && exactMatchInScope) {
        // Found exact match - check if it should be excluded
        if (excludeLocation && exactMatch.binCode.toLowerCase() === excludeLocation.toLowerCase()) {
          setLocations([]);
          setError(`Lokasjon "${exactMatch.binCode}" er allerede valgt som fra-lokasjon`);
        } else {
          setLocations([exactMatch]);
        }
        setHasSearched(true);
        return;
      }

      // No exact match - fall back to general search (slower but finds partial matches)
      const results = await stcBinService.search(companyNo, term, warehouseID);

      // Filter out excluded location if specified
      const filteredResults = excludeLocation
        ? results.filter(bin => bin.binCode.toLowerCase() !== excludeLocation.toLowerCase())
        : results;

      if (filteredResults.length === 0 && exactMatch && warehouseID) {
        // Lokasjonen finnes, men i et annet lager enn det operatøren har valgt
        setError(
          `Lokasjon "${exactMatch.binCode}" ligger i lager ${exactMatch.warehouseID}, ikke i ${warehouseLabel ?? `lager ${warehouseID}`}`
        );
      }

      setLocations(filteredResults);
      setHasSearched(true);
    } catch (err) {
      console.error('Search error:', err);
      setError('Feil ved søk. Vennligst prøv igjen.');
      setLocations([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, [excludeLocation, minSearchLength, warehouseID, warehouseLabel]);

  // Handle search input with debounce
  const handleSearchChange = (value: string, companyNo: string) => {
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search (reduced to 150ms for faster response)
    debounceTimerRef.current = setTimeout(() => {
      performSearch(value, companyNo);
    }, 150);
  };

  // Handle location selection
  const handleLocationSelect = (bin: StcBinDto) => {
    const locationResult: LocationSearchResult = {
      binCode: bin.binCode,
      locationName: bin.binCode,
      warehouseID: bin.warehouseID,
      description: bin.description1,
      zoneID: bin.stcZoneID,
      binSizeNo: bin.binSizeNo,
      binTypeNo: bin.binTypeNo,
      binGrpNo: bin.binGrpNo
    };

    onSelect(locationResult);
    onClose();
  };

  // Handle click on backdrop
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Get company number from auth context
  const companyNo = user?.companyNo || '';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <MapPin className="h-6 w-6 text-blue-500" />
              <h2 className="text-lg font-semibold">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Lukk"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-6 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value, companyNo)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim().length >= minSearchLength) {
                    // Trykk Enter for øyeblikkelig søk (bypass debounce)
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                    }
                    performSearch(searchTerm, companyNo);
                  }
                }}
                placeholder={
                  minSearchLength > 1
                    ? `Skriv minst ${minSearchLength} tegn — søker automatisk...`
                    : 'Søk på lokasjonsnavn, beskrivelse eller sone... (Enter for øyeblikkelig søk)'
                }
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {searchTerm && searchTerm.trim().length < minSearchLength && (
              <div className="mt-2 text-sm text-gray-600">
                Skriv minst {minSearchLength} tegn for å søke...
              </div>
            )}

            {searchTerm && searchTerm.trim().length >= minSearchLength && (
              <div className="mt-2 text-sm text-gray-600">
                Søker automatisk mens du skriver... (Trykk <kbd className="px-1.5 py-0.5 bg-gray-200 border border-gray-300 rounded text-xs">Enter</kbd> for øyeblikkelig søk)
              </div>
            )}

            {excludeLocation && (
              <div className="mt-2 text-sm text-orange-600 flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                Lokasjon {excludeLocation} er ekskludert fra søket
              </div>
            )}

            {warehouseID && (
              <div className="mt-2 text-sm text-blue-600 flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                Viser kun lokasjoner i {warehouseLabel ?? `lager ${warehouseID}`}
              </div>
            )}
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader className="animate-spin h-10 w-10 text-blue-500 mb-3" />
                <span className="text-gray-600">Søker...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-500 mb-2">
                  <X className="h-12 w-12 mx-auto mb-3" />
                </div>
                <p className="text-gray-700 font-medium">{error}</p>
              </div>
            ) : !hasSearched ? (
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">Skriv i søkefeltet for å finne lokasjoner</p>
                <p className="text-sm text-gray-400 mt-2">
                  Du kan søke på lokasjonsnavn, beskrivelse eller sone-ID
                </p>
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">Ingen lokasjoner funnet</p>
                <p className="text-sm text-gray-400 mt-2">
                  Prøv et annet søkeord
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-gray-600 mb-4">
                  Fant {locations.length} lokasjon{locations.length !== 1 ? 'er' : ''}:
                </div>

                {locations.map((bin) => (
                  <div
                    key={bin.id}
                    onClick={() => handleLocationSelect(bin)}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <MapPin className="h-6 w-6 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-xl text-gray-900 group-hover:text-blue-700">
                            {bin.binCode}
                          </div>

                          {bin.description1 && (
                            <div className="text-sm text-gray-600 mt-1">
                              {bin.description1}
                            </div>
                          )}

                          {/* Additional details */}
                          <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-500">
                            {bin.warehouseID && (
                              <div className="flex items-center">
                                <span className="font-medium mr-1">Lager:</span>
                                <span>{bin.warehouseID}</span>
                              </div>
                            )}

                            {bin.stcZoneID && (
                              <div className="flex items-center">
                                <span className="font-medium mr-1">Sone:</span>
                                <span>{bin.stcZoneID}</span>
                              </div>
                            )}

                            {bin.itemNo && (
                              <div className="flex items-center">
                                <span className="font-medium mr-1">Dedikert til:</span>
                                <span>{bin.itemNo}</span>
                              </div>
                            )}

                            {bin.pickableYesNo !== undefined && (
                              <div className="flex items-center">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  bin.pickableYesNo
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {bin.pickableYesNo ? 'Plukkbar' : 'Ikke plukkbar'}
                                </span>
                              </div>
                            )}

                            {bin.sharedBinYesNo && (
                              <div className="flex items-center">
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                  Delt lokasjon
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-6 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              Trykk ESC for å lukke
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Avbryt
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
